import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { CompactModuleFilter } from "../../utils/RelatedEntries/CompactModuleFilter";
import { useRelatedCollection } from "../../utils/RelatedEntries/useRelatedCollection";
import { RelatedEntries } from "../../utils/RelatedEntries/RelatedEntries";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../utils/PMTableView/RightDrawer";
import {
  mergeScopedFilters,
  useScopeFiltersSync,
  computeHasMore,
  embeddedFetchKey,
  scopeFiltersKey,
} from "../../utils/PMTableView/embedded-table-utils";
import {
  DeliveryAddressTitle,
  ItemStatus,
  dateToString,
} from "../../Utils";
import { getProjectsAsync } from "../../../slices/project";
import { buildProjectsIndexParams } from "../../../utils/projectsIndexParams";

export const ProjectsStackList = (props) => {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return <ProjectsStackListContent key={JSON.stringify([companyId, props.module, props.filters])} {...props} />;
};

const ProjectsStackListContent = (props) => {
  const { filters: scopeFilters = [], module: moduleName = "embedded" } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [itemState, loadItems] = useRelatedCollection(getProjectsAsync, JSON.stringify(scopeFilters));
  const { isLoading, totalCount } = itemState;

  const loadMoreInFlightRef = useRef(false);
  const [drawerProject, setDrawerProject] = useState(null);

  const [controller, setController] = useState(() => ({
    filters: mergeScopedFilters(scopeFilters, []),
    page: 1,
    per_page: props.per_page ? parseInt(props.per_page, 10) : 25,
    query: "",
    sort: "desc",
    sortBy: props.sortBy || "created_at",
    view: "all",
    offer_scope: "projects",
    activeOnly: false,
    ...(props.fields ? { fields: `${props.fields},created_at,project_number,name,status,customer,delivery_address` } : {}),
  }));

  useScopeFiltersSync(scopeFilters, setController);

  const handleFiltersChange = (filters) => {
    const userFilters = [];
    if (filters.statuses?.length > 0) {
      userFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }
    if (filters.with_customer) {
      userFilters.push({ name: "with_customer", value: "true" });
    }
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      activeOnly: !!filters.active,
      filters: mergeScopedFilters(scopeFilters, userFilters),
    }));
  };

  const getInitialFilters = () => {
    const initial = {
      query: controller.query || "",
      statuses: [],
      active: controller.activeOnly === true,
      with_customer: false,
    };
    controller.filters.forEach((filter) => {
      if ((scopeFilters || []).some((sf) => sf.name === filter.name)) return;
      switch (filter.name) {
        case "status":
          try {
            initial.statuses = JSON.parse(filter.value);
          } catch {
            initial.statuses = [filter.value];
          }
          break;
        case "with_customer":
          initial.with_customer = filter.value === "true";
          break;
        default:
          break;
      }
    });
    return initial;
  };

  const projectsQueryParams = useMemo(
    () =>
      buildProjectsIndexParams(controller, {
        activeOnly: controller.activeOnly !== false,
      }),
    [controller]
  );

  const handleLoadMore = useCallback(() => {
    const loaded = itemState?.data?.length || 0;
    const total = totalCount || 0;
    if (loadMoreInFlightRef.current || isLoading || loaded >= total) return;

    const nextPage = (controller.page || 1) + 1;
    loadMoreInFlightRef.current = true;
    loadItems({
        ...projectsQueryParams,
        page: nextPage,
        append: true,
      }).then((loaded) => { if (loaded) setController((c) => ({ ...c, page: nextPage })); }).finally(() => {
      loadMoreInFlightRef.current = false;
    });
  }, [
    projectsQueryParams,
    loadItems,
    isLoading,
    itemState?.data?.length,
    totalCount,
  ]);

  const debouncedFetch = useMemo(
    () => debounce((ctrl) => loadItems(ctrl), 250),
    [loadItems]
  );

  const filtersKey = scopeFiltersKey(controller.filters);
  const fetchKey = useMemo(
    () =>
      embeddedFetchKey(controller, {
        offer_scope: controller.offer_scope,
        activeOnly: controller.activeOnly,
      }),
    [
      filtersKey,
      controller.query,
      controller.sort,
      controller.sortBy,
      controller.per_page,
      controller.view,
      controller.offer_scope,
      controller.fields,
      controller.activeOnly,
    ]
  );

  useEffect(() => {
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
    debouncedFetch({ ...projectsQueryParams, page: 1 });
    return () => debouncedFetch.cancel();
  }, [fetchKey, debouncedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataLength = itemState?.data?.length || 0;
  const hasMore = computeHasMore({ isLoading, dataLength, totalCount });

  const projectAcceptedStatuses = [
    "draft",
    "ongoing",
    "finished",
    "cancelled",
    "on_hold",
    "planning",
  ];

  return (
    <div className="min-w-0 space-y-4">
      <CompactModuleFilter
        disabled={isLoading}
        onFiltersChange={handleFiltersChange}
        initialFilters={getInitialFilters()}
        searchPlaceholder={t("module_overview.search_projects")}
        filterTitle={t("module_overview.filter_projects")}
        acceptedStatuses={projectAcceptedStatuses}
        customCheckboxButtonFilters={[
          {
            key: "active",
            label: t("Active Projects"),
            icon: CheckCircleIcon,
            defaultValue: false,
          },
        ]}
      />

      <div className="min-w-0">
        <RelatedEntries kind="projects" context={moduleName} data={itemState.data} isLoading={isLoading} error={itemState.error} totalCount={totalCount}
          onRetry={() => { setController((value) => ({ ...value, page: 1 })); loadItems({ ...projectsQueryParams, page: 1 }); }}
          onRowClick={setDrawerProject} renderStatus={(item) => <ItemStatus item={item} />} hasMore={hasMore} onLoadMore={handleLoadMore}
        />
      </div>

      <RightDrawer
        open={!!drawerProject}
        onClose={() => setDrawerProject(null)}
        eyebrow={
          drawerProject ? `${t("Number")} ${drawerProject.project_number || ""}` : ""
        }
        title={drawerProject?.name || drawerProject?.displayTitle || t("Projects")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/projects/${drawerProject.id}`),
          },
        ]}
      >
        {drawerProject && (
          <>
            <DrawerSection title={t("Details")}>
              <DrawerProps
                items={[
                  { label: t("Status"), value: <ItemStatus item={drawerProject} /> },
                  {
                    label: t("Customer"),
                    value:
                      drawerProject.customer?.displayTitle ||
                      drawerProject.customer?.name ||
                      "—",
                  },
                  {
                    label: t("Address"),
                    value: drawerProject?.delivery_address?.id ? (
                      <DeliveryAddressTitle
                        deliveryAddress={drawerProject.delivery_address}
                      />
                    ) : (
                      "—"
                    ),
                  },
                  {
                    label: t("Created"),
                    value: drawerProject.created_at
                      ? dateToString(drawerProject.created_at)
                      : "—",
                  },
                ]}
              />
            </DrawerSection>
            {drawerProject.description && (
              <DrawerSection title={t("Description")}>
                <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-slate-600">
                  {drawerProject.description}
                </p>
              </DrawerSection>
            )}
          </>
        )}
      </RightDrawer>
    </div>
  );
};

ProjectsStackList.defaultProps = {
  filters: [],
  sort: "desc",
  sortBy: "created_at",
};
